import * as React from 'react';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Header from './components/Header';
import SideMenu from './components/SideMenu';
import MobileAppShell from './mobile/MobileAppShell';
import AppTheme from '../shared-theme/AppTheme';
import { withAuthGuard } from '../hocs/with-auth-guard';
import { MOBILE_MEDIA_QUERY } from '../hooks/use-is-mobile';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const mobileThemeComponents = {
  ...xThemeComponents,
  MuiDialog: {
    defaultProps: {
      fullScreen: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': {
          borderRadius: '10px',
          border: '1px solid',
          borderColor: (theme.vars || theme).palette.divider,
        },
      }),
      paperFullScreen: {
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      },
    },
  },
};

const contentShellSx = {
  width: '100%',
  maxWidth: 1700,
  mx: 'auto',
  px: { xs: 2, sm: 3, md: 4 },
};

function DesktopShell({ headerTitle, children }) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <SideMenu />
      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          overflow: 'hidden',
          backgroundColor: theme.vars
            ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
            : alpha(theme.palette.background.default, 1),
        })}
      >
        <Box
          sx={{
            ...contentShellSx,
            flexShrink: 0,
            pt: 2,
            pb: 1,
          }}
        >
          <Header headerTitle={headerTitle} />
        </Box>
        <Box
          sx={{
            ...contentShellSx,
            flex: 1,
            overflow: 'auto',
            pb: 5,
            pt: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

const Dashboard = withAuthGuard(({ children, headerTitle, ...props }) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });

  return (
    <AppTheme {...props} themeComponents={isMobile ? mobileThemeComponents : xThemeComponents}>
      <CssBaseline enableColorScheme />
      {isMobile ? (
        <MobileAppShell headerTitle={headerTitle}>{children}</MobileAppShell>
      ) : (
        <DesktopShell headerTitle={headerTitle}>{children}</DesktopShell>
      )}
    </AppTheme>
  );
})

export default Dashboard;
